import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useIssuesStore } from "@/stores/pm/hooks/usePMStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PMLoadingState } from "@/domains/pm/components/shared/PMLoadingState";
import { ActorAvatar } from "@/domains/pm/components/shared/ActorAvatar";
import { renderActivityLine } from "@/domains/pm/utils/activityLine";
import { ACTIVITY_ICON } from "@/domains/pm/utils/activityMeta";

interface IssueActivityTimelineProps {
  issueId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Full date + time, e.g. 17.06.2026 14:32. */
function formatDateTime(value: string, language: "en" | "et"): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(language === "et" ? "et-EE" : "en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * WHA-3732: the "Issue history" dialog. Renders the full change timeline for
 * one issue (creation + every attribute change), oldest first. Lines are
 * produced by the pure renderActivityLine helper from snapshotted activity rows.
 */
export const IssueActivityTimeline = observer(function IssueActivityTimeline({
  issueId,
  open,
  onOpenChange,
}: IssueActivityTimelineProps) {
  const issuesStore = useIssuesStore();
  const { t, language } = useLanguage();

  useEffect(() => {
    if (open && issueId) {
      void issuesStore.loadActivity(issueId);
    }
  }, [open, issueId, issuesStore]);

  const rows = issuesStore.selectedIssueActivity;
  const loading = issuesStore.isLoadingActivity;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t.pm.activity.historyTitle}</DialogTitle>
        </DialogHeader>

        {loading && rows.length === 0 ? (
          <PMLoadingState />
        ) : rows.length === 0 ? (
          <p className="px-1 py-8 text-center text-sm text-muted-foreground">
            {t.pm.activity.historyEmpty}
          </p>
        ) : (
          <ScrollArea className="max-h-[60vh] pr-3">
            <ol className="space-y-3">
              {rows.map((a) => {
                const TypeIcon = ACTIVITY_ICON[a.action_type];
                return (
                  <li key={a.id} className="flex items-start gap-3">
                    {/* Actor avatar anchors the row; type icon flags the kind. */}
                    <ActorAvatar actor={a.actor} size={28} className="mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="flex items-start gap-1.5 text-sm leading-snug text-foreground">
                        <TypeIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                        <span>{renderActivityLine(a, t, language)}</span>
                      </p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {formatDateTime(a.created_at, language)}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
});
